import type { ParkingSlot } from '@/components/feature/parking/types';
import { CustomMenuCategoryCode } from '@/constants/custom-menu-category-code';
import { BuildingMenuType } from '@/enums';
import { useConfigStore } from '@/stores';
import type { DabeeoMapData } from 'dabeeomaps';
import { useMemo, useState } from 'react';

const koreanCollator = new Intl.Collator('ko', { numeric: true, sensitivity: 'base' });

interface UseParkingSlotsOptions {
    mapData: DabeeoMapData | undefined;
    gnbMenuId?: string;
}

export const useParkingSlots = ({ mapData, gnbMenuId }: UseParkingSlotsOptions) => {
    const buildingMenus = useConfigStore((state) => state.config.server?.buildingMenus);
    const language = useConfigStore((state) => state.config.ui.language);

    const [selectedSlotId, setSelectedSlotId] = useState<string | undefined>(undefined);

    const parkingSlots = useMemo<ParkingSlot[]>(() => {
        const menus = buildingMenus?.[language] ?? [];

        const parkingMenu = gnbMenuId
            ? menus.find((menu) => menu.id === gnbMenuId)
            : menus.find((menu) => menu.menuCode === CustomMenuCategoryCode.PARKING && menu.menuType === BuildingMenuType.CUSTOM_MENU);
        const selectedCategories = parkingMenu?.selectedCategories ?? [];
        if (!selectedCategories.length) return [];

        const categoryCodes = new Set(
            selectedCategories.flatMap((category) => (category.mergeCategoryCode?.length ? category.mergeCategoryCode : [category.categoryCode]))
        );

        const allPois = mapData?.dataPoi?.getPois?.() ?? [];
        const filtered = allPois.filter((poi) => {
            if (Array.isArray(poi.categoryCode)) {
                return poi.categoryCode.some((code) => categoryCodes.has(code));
            }
            return categoryCodes.has(poi.categoryCode);
        });

        filtered.sort((a, b) => koreanCollator.compare(a.titleByLanguages.find((tl) => tl.lang === language)?.text ?? a.title, b.titleByLanguages.find((tl) => tl.lang === language)?.text ?? b.title));

        return filtered.map((poi) => ({
            id: poi.id,
            name: poi.titleByLanguages.find((tl) => tl.lang === language)?.text ?? poi.title,
            floorId: poi.floorId,
        }));
    }, [buildingMenus, language, mapData, gnbMenuId]);

    return { parkingSlots, selectedSlotId, setSelectedSlotId };
};
